// Job Title Selection Step
import React from 'react';
import { Briefcase, Search } from 'lucide-react';

interface JobTitleStepProps {
  jobTitle: string;
  onJobTitleChange: (jobTitle: string) => void;
}

const commonJobTitles = [
  'Software Engineer',
  'Frontend Developer',
  'Backend Developer',
  'Full Stack Developer',
  'Data Scientist',
  'Data Analyst',
  'Product Manager',
  'UX/UI Designer',
  'DevOps Engineer',
  'Machine Learning Engineer',
  'Software Architect',
  'Technical Lead',
  'Quality Assurance Engineer',
  'Business Analyst',
  'Project Manager',
  'Cybersecurity Analyst',
  'Database Administrator',
  'Cloud Engineer',
  'Mobile App Developer',
  'Sales Manager'
];

export const JobTitleStep: React.FC<JobTitleStepProps> = ({
  jobTitle,
  onJobTitleChange
}) => {
  const filteredTitles = commonJobTitles.filter(title =>
    title.toLowerCase().includes(jobTitle.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Briefcase className="w-8 h-8 text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          What position are you interviewing for?
        </h2>
        <p className="text-gray-600">
          This helps us tailor the AI responses to your specific role and industry.
        </p>
      </div>

      <div className="max-w-md mx-auto">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={jobTitle}
            onChange={(e) => onJobTitleChange(e.target.value)}
            placeholder="Search or type your job title..."
            className="input-field pl-10"
            autoFocus
          />
        </div>

        {jobTitle && filteredTitles.length > 0 && (
          <div className="mt-4 max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-white shadow-sm">
            {filteredTitles.slice(0, 8).map((title) => (
              <button
                key={title}
                onClick={() => onJobTitleChange(title)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
              >
                <div className="flex items-center">
                  <Briefcase className="w-4 h-4 text-gray-400 mr-3" />
                  <span className="text-gray-900">{title}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {!jobTitle && (
          <div className="mt-6">
            <p className="text-sm text-gray-500 mb-3">Popular job titles:</p>
            <div className="grid grid-cols-2 gap-2">
              {commonJobTitles.slice(0, 10).map((title) => (
                <button
                  key={title}
                  onClick={() => onJobTitleChange(title)}
                  className="text-left px-3 py-2 text-sm text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {title}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {jobTitle && (
        <div className="max-w-md mx-auto p-4 bg-primary-50 rounded-lg">
          <p className="text-sm text-primary-800">
            <strong>Selected:</strong> {jobTitle}
          </p>
          <p className="text-xs text-primary-600 mt-1">
            The AI will customize responses based on this role's typical requirements and expectations.
          </p>
        </div>
      )}
    </div>
  );
};
